/**
 * Event Details: choose Export CSV vs Print and which attendance statuses to include.
 */
import React, { useEffect, useMemo, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import {
  countContactRosterByStatus,
  filterContactRosterParticipants,
} from "../shared/utils/eventParticipantContactRoster";

const fieldsetStyle = {
  border: "1px solid #dee2e6",
  borderRadius: "6px",
  padding: "10px 12px",
  margin: "0 0 10px 0",
};
const legendStyle = { fontWeight: 600, fontSize: "12px", color: "#495057", padding: "0 4px" };
const optionRowStyle = { display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", fontSize: "13px" };
const hintStyle = { fontSize: "12px", color: "#6c757d", margin: "4px 0 0 0" };

const DEFAULT_OPTS = {
  action: "csv", // "csv" | "print"
  includeRegistered: true,
  includeWaitlisted: false,
  includeCancelled: false,
};

/**
 * @param {{
 *   open: boolean,
 *   participants?: Record<string, unknown>[],
 *   selectedParticipantIds?: Set<string>,
 *   onCancel: () => void,
 *   onConfirm: (opts: { action: 'csv'|'print', includeRegistered: boolean, includeWaitlisted: boolean, includeCancelled: boolean, rows: Record<string, unknown>[] }) => void | Promise<void>,
 * }} props
 */
function EventParticipantContactRosterModal({
  open,
  participants = [],
  selectedParticipantIds,
  onCancel,
  onConfirm,
}) {
  const [action, setAction] = useState(DEFAULT_OPTS.action);
  const [includeRegistered, setIncludeRegistered] = useState(DEFAULT_OPTS.includeRegistered);
  const [includeWaitlisted, setIncludeWaitlisted] = useState(DEFAULT_OPTS.includeWaitlisted);
  const [includeCancelled, setIncludeCancelled] = useState(DEFAULT_OPTS.includeCancelled);

  useEffect(() => {
    if (!open) return;
    setAction(DEFAULT_OPTS.action);
    setIncludeRegistered(DEFAULT_OPTS.includeRegistered);
    setIncludeWaitlisted(DEFAULT_OPTS.includeWaitlisted);
    setIncludeCancelled(DEFAULT_OPTS.includeCancelled);
  }, [open]);

  const counts = useMemo(() => countContactRosterByStatus(participants), [participants]);

  const selectedKey = useMemo(() => {
    if (!(selectedParticipantIds instanceof Set) || selectedParticipantIds.size === 0) return "";
    return Array.from(selectedParticipantIds)
      .map((id) => String(id))
      .sort()
      .join(",");
  }, [selectedParticipantIds]);
  const hasRowSelection = selectedKey.length > 0;

  const previewRows = useMemo(
    () =>
      filterContactRosterParticipants(participants, {
        includeRegistered,
        includeWaitlisted,
        includeCancelled,
        selectedIds: hasRowSelection ? selectedKey.split(",") : null,
      }),
    [participants, includeRegistered, includeWaitlisted, includeCancelled, hasRowSelection, selectedKey]
  );

  const anyStatus = includeRegistered || includeWaitlisted || includeCancelled;
  const confirmLabel = action === "print" ? "Print" : "Export CSV";

  return (
    <ConfirmationModal
      open={open}
      title="Print / Export Roster"
      mode="confirm"
      contentMaxWidth={480}
      confirmLabel={confirmLabel}
      confirmDisabled={!anyStatus || previewRows.length === 0}
      cancelLabel="Cancel"
      onCancel={onCancel}
      onConfirm={() =>
        onConfirm({
          action,
          includeRegistered,
          includeWaitlisted,
          includeCancelled,
          rows: previewRows,
        })
      }
      message={
        <div>
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Action</legend>
            <label style={optionRowStyle}>
              <input
                type="radio"
                name="contact-roster-action"
                checked={action === "csv"}
                onChange={() => setAction("csv")}
              />
              Export CSV
            </label>
            <label style={{ ...optionRowStyle, marginBottom: 0 }}>
              <input
                type="radio"
                name="contact-roster-action"
                checked={action === "print"}
                onChange={() => setAction("print")}
              />
              Print
            </label>
          </fieldset>

          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Include</legend>
            <label style={optionRowStyle}>
              <input
                type="checkbox"
                checked={includeRegistered}
                onChange={(e) => setIncludeRegistered(e.target.checked)}
              />
              Registered ({counts.registered})
            </label>
            <label style={optionRowStyle}>
              <input
                type="checkbox"
                checked={includeWaitlisted}
                onChange={(e) => setIncludeWaitlisted(e.target.checked)}
              />
              Waitlisted ({counts.waitlisted})
            </label>
            <label style={{ ...optionRowStyle, marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={includeCancelled}
                onChange={(e) => setIncludeCancelled(e.target.checked)}
              />
              Cancelled ({counts.cancelled})
            </label>
          </fieldset>

          <p style={hintStyle}>
            {hasRowSelection
              ? `Using ${selectedKey.split(",").length} selected row(s) within the statuses above.`
              : "No rows selected — all participants in the statuses above will be included."}
          </p>
          <p style={{ ...hintStyle, marginTop: 2 }}>
            {anyStatus
              ? `${previewRows.length} participant${previewRows.length === 1 ? "" : "s"} will be included.`
              : "Select at least one status."}
          </p>
        </div>
      }
    />
  );
}

export default EventParticipantContactRosterModal;
